import { Box, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LayersOutlined } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { useAppTFunction } from '@retail/app/i18n';
import { useSelectedOrgUnit, useUserBrands } from '@retail/app/stores/selected-context';
import { useAgreements } from '@retail/purchase-agreements/context';
import { useFetchPriceRoundingRules } from '@retail/rounding/data-access';
import { PriceRoundingRuleConfig } from '@retail/rounding/types';
import { getPriceSuggestionType } from '@retail/products/utils';
import { PriceSuggestionType } from '@retail/products/types';
import { Image } from '@retail/components';
import { brandLogo, getLLDate } from '@retail/utils';
import { AllConditionsFormValues, ConditionStepType } from '../allConditionsSchema';
import { ConditionSummaryCard } from './ConditionSummaryCard';

interface Props {
  conditionSteps: ConditionStepType[];
  supplierNumber?: number;
  assortmentName: string;
}

export function SummaryStep({ conditionSteps, supplierNumber, assortmentName }: Props) {
  const t = useCalculusTFunction();
  const appT = useAppTFunction();
  const { watch } = useFormContext<AllConditionsFormValues>();
  const values = watch();

  const { assortmentId } = useSelectedOrgUnit();
  const { selectedBrand } = useUserBrands();
  const { agreements } = useAgreements({
    supplierNumber: supplierNumber ?? 0,
    enabled: !!supplierNumber,
  });
  const showRounding = conditionSteps.includes('rounding');
  const { data: roundingRules = [] } = useFetchPriceRoundingRules({
    assortmentId,
    suspense: false,
    enabled: showRounding,
  });

  const validFrom = getLLDate(values.fromDate);

  const levelSummary = (() => {
    switch (values.level) {
      case 'assortment':
        return assortmentName;
      case 'supplier':
        return t('calculus.level.supplier');
      case 'productGroup':
        return (
          (
            (values.productGroup ?? values.mainGroup ?? values.overGroup) as
              | { getLabel?: () => string }
              | null
              | undefined
          )?.getLabel?.() ?? t('calculus.level.productGroup')
        );
      case 'discountGroup':
        return values.discountGroups?.map((dg: { code?: string }) => dg.code).join(', ');
      case 'item':
        return (
          (values.item as { primaryText?: string } | null | undefined)?.primaryText ??
          t('calculus.level.item.label')
        );
      default:
        return '';
    }
  })();

  const purchaseFilled = !values.purchaseSkipped && !!values.agreementId;
  const freightValue = values.percentAddon
    ? `${values.percentAddon}%`
    : values.fixedAddon
    ? appT('freight.columns.pricePerUnit', {
        price: values.fixedAddon,
        unit: values.fixedAddonUnit ?? '',
      })
    : undefined;
  const freightFilled = !values.freightSkipped && !!freightValue;

  const priceSourcePriorities = (values.priceSourcePriority?.filter(Boolean) ??
    []) as PriceSuggestionType[];
  const salesPriceFilled = !values.salesPriceSkipped && priceSourcePriorities.length > 0;
  const orderedPriorities = priceSourcePriorities.includes('MARKUP')
    ? priceSourcePriorities
    : [...priceSourcePriorities, 'MARKUP' as PriceSuggestionType];

  const selectedRounding = roundingRules
    .map((dto) =>
      PriceRoundingRuleConfig.fromDto(dto, {
        name: selectedBrand.name,
        id: selectedBrand.brandId,
        imgUrl: brandLogo(selectedBrand.brandId).logo,
      })
    )
    .find((rule) => rule.id === Number(values.priceRoundingId));
  const roundingFilled = !values.roundingSkipped && !!values.priceRoundingId;

  return (
    <Stack gap={2}>
      <Paper
        variant="outlined"
        sx={(theme) => ({
          p: 2,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderColor: theme.vars
            ? `rgba(${theme.vars.palette.primary.mainChannel} / 0.4)`
            : alpha(theme.palette.primary.main, 0.4),
          backgroundColor: theme.vars
            ? `rgba(${theme.vars.palette.primary.mainChannel} / 0.05)`
            : alpha(theme.palette.primary.main, 0.05),
        })}
      >
        <Box
          sx={(theme) => ({
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.vars
              ? `rgba(${theme.vars.palette.primary.mainChannel} / 0.15)`
              : alpha(theme.palette.primary.main, 0.15),
            color: 'primary.main',
            ...theme.applyStyles('dark', { color: 'primary.light' }),
          })}
        >
          <LayersOutlined />
        </Box>
        <Stack>
          <Typography variant="caption" color="text.secondary">
            {t('calculus.createAllConditions.steps.level')}
          </Typography>
          <Typography variant="subtitle1">{levelSummary}</Typography>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2,
        }}
      >
        {conditionSteps.includes('purchase') && (
          <ConditionSummaryCard
            step="purchase"
            filled={purchaseFilled}
            validFrom={validFrom}
            value={
              <Stack gap={0.5}>
                <Typography variant="subtitle1">
                  {agreements.find((agreement) => agreement.id === Number(values.agreementId))
                    ?.name ?? `#${values.agreementId}`}
                </Typography>
                {values.priceType && (
                  <Chip
                    label={`${appT('purchaseAgreements.agreement.actions.priceType')}: ${
                      values.priceType
                    }`}
                    size="small"
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start' }}
                  />
                )}
              </Stack>
            }
          />
        )}

        {conditionSteps.includes('freight') && (
          <ConditionSummaryCard
            step="freight"
            filled={freightFilled}
            validFrom={validFrom}
            value={<Typography variant="h6">{freightValue}</Typography>}
          />
        )}

        <ConditionSummaryCard
          step="salesPrice"
          filled={salesPriceFilled}
          validFrom={validFrom}
          value={
            <Stack gap={0.5}>
              {orderedPriorities.map((priority, index) => (
                <Stack key={priority} direction="row" alignItems="center" gap={1}>
                  <Typography
                    variant="caption"
                    sx={(theme) => ({
                      width: 20,
                      height: 20,
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: theme.vars
                        ? `rgba(${theme.vars.palette.success.mainChannel} / 0.15)`
                        : alpha(theme.palette.success.main, 0.15),
                      color: 'success.main',
                      fontWeight: 'bold',
                    })}
                  >
                    {index + 1}
                  </Typography>
                  <Typography variant="body2">
                    {priority === 'MARKUP' && values.markupCoverageDegree
                      ? `${appT('markup.markup')}: ${values.markupCoverageDegree}%`
                      : getPriceSuggestionType(appT, priority)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          }
        />

        {showRounding && (
          <ConditionSummaryCard
            step="rounding"
            filled={roundingFilled}
            validFrom={validFrom}
            value={
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="subtitle1">
                  {selectedRounding?.name ?? `#${values.priceRoundingId}`}
                </Typography>
                {selectedRounding?.centralOwner && (
                  <Tooltip
                    title={appT('rounding.ruleOwner.central', {
                      owner: selectedRounding.centralOwner.name,
                    })}
                  >
                    <Image
                      src={selectedRounding.centralOwner.imgUrl}
                      alt={selectedRounding.centralOwner.name}
                      width={28}
                      height={28}
                    />
                  </Tooltip>
                )}
              </Stack>
            }
          />
        )}
      </Box>
    </Stack>
  );
}
